// src/components/Services/Services_ITManagement.jsx
import React from "react";
import { motion } from "framer-motion";
import itGif from "../images/photos/it.gif"; // ✅ Ensure correct path

// Page animation variants
const pageVariants = {
  initial: { rotateY: 90, opacity: 0 },
  in: { rotateY: 0, opacity: 1 },
  out: { rotateY: -90, opacity: 0 },
};

const pageTransition = {
  type: "spring",
  stiffness: 80,
  damping: 20,
};

const fadeUp = {
  hidden: { y: 10, opacity: 0 },
  visible: (i = 0) => ({
    y: 0,
    opacity: 1,
    transition: { delay: 0.06 * i },
  }),
};

export default function Services_ITManagement() {
  const services = [
    "Android Zero Touch Enrollment",
    "Android Management",
    "Cloud Computing",
    "Cloud Management Service",
    "Cyber Security",
    "iOS Management",
    "Mobile Management",
    "IT Management",
    "IT Support",
    "Software Development",
    "Digital Marketing",
  ];

  return (
    <motion.div
      initial="initial"
      animate="in"
      exit="out"
      variants={pageVariants}
      transition={pageTransition}
      style={{
        minHeight: "100vh",
        background: "#040d1fff",
        color: "#e5e9edff",
        padding: "32px 20px",
      }}
    >
      <div style={{ maxWidth: 1100, margin: "0 auto" }}>
        {/* HERO SECTION */}
        <section
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 32,
            alignItems: "center",
          }}
        >
          {/* LEFT - GIF */}
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeUp}
            custom={0}
            style={{ textAlign: "center" }}
          >
            <img
              src={itGif}
              alt="IT Management"
              style={{
                width: "100%",
                maxWidth: 480,
                height: "auto",
                borderRadius: 12,
                boxShadow: "0 10px 30px rgba(0,0,0,0.5)",
              }}
              loading="lazy"
            />
          </motion.div>

          {/* RIGHT - TEXT */}
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeUp}
            custom={1}
          >
            <h1
              style={{
                marginTop: 0,
                fontSize: "clamp(1.8rem, 3vw, 2.5rem)",
                color: "#6dd4e8ff",
              }}
            >
              IT Management
            </h1>
            <p
              style={{
                lineHeight: 1.6,
                color: "#e2e8f0",
                fontSize: "clamp(0.95rem, 1.8vw, 1.1rem)",
              }}
            >
              Our IT Management services help organizations oversee and optimize
              their entire IT infrastructure. From strategy and planning to
              operations, security, and support, we ensure that technology
              enables business growth, compliance, and innovation.
            </p>

            <h3
              style={{
                marginTop: 16,
                color: "#53bdd2ff",
                fontSize: "clamp(1.2rem, 2.5vw, 1.5rem)",
              }}
            >
              Key IT Management Services
            </h3>
            <ul
              style={{
                lineHeight: 1.7,
                color: "#f1f5f9",
                paddingLeft: 18,
                fontSize: "clamp(0.9rem, 1.8vw, 1rem)",
              }}
            >
              <li>
                <strong>IT Strategy & Planning</strong> — aligning IT goals with
                business objectives.
              </li>
              <li>
                <strong>Infrastructure Management</strong> — monitoring servers,
                storage, and networks.
              </li>
              <li>
                <strong>Cybersecurity</strong> — firewalls, encryption, and
                compliance enforcement.
              </li>
              <li>
                <strong>Cloud Management</strong> — hybrid and multi-cloud
                optimization.
              </li>
              <li>
                <strong>Data Management</strong> — secure backup, governance,
                and recovery.
              </li>
              <li>
                <strong>IT Service Management (ITSM)</strong> — helpdesk,
                incident, and change management.
              </li>
              <li>
                <strong>Governance & Compliance</strong> — IT audits, standards,
                and risk management.
              </li>
            </ul>

            {/* Buttons */}
            <div
              style={{
                marginTop: 18,
                display: "flex",
                gap: 12,
                flexWrap: "wrap",
              }}
            >
              <a
                href="mailto:support@zyntro.in"
                style={{
                  background: "#f69f48ff",
                  color: "white",
                  padding: "10px 16px",
                  borderRadius: 8,
                  textDecoration: "none",
                  fontWeight: 600,
                  fontSize: "clamp(0.85rem, 1.6vw, 1rem)",
                }}
              >
                Email Support
              </a>
              <a
                href="tel:+919956353236"
                style={{
                  border: "1px solid #4e4e54ff",
                  color: "#010101ff",
                  padding: "10px 16px",
                  borderRadius: 8,
                  textDecoration: "none",
                  fontWeight: 600,
                  background: "white",
                  fontSize: "clamp(0.85rem, 1.6vw, 1rem)",
                }}
              >
                Call +91-9956353236
              </a>
            </div>
          </motion.div>
        </section>

        {/* OTHER SERVICES */}
        <section style={{ marginTop: 50 }}>
          <h3
            style={{
              color: "#4fe1f4ff",
              fontSize: "clamp(1.3rem, 3vw, 1.7rem)",
              marginBottom: 18,
              textAlign: "center",
            }}
          >
            Other Services
          </h3>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
              gap: 14,
            }}
          >
            {services.map((s) => (
              <div
                key={s}
                style={{
                  background: "#1e293b",
                  padding: 12,
                  borderRadius: 8,
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
                }}
              >
                <div
                  style={{
                    width: 34,
                    height: 34,
                    borderRadius: 8,
                    background: "#1e7d95ff",
                    color: "white",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: 700,
                    fontSize: "0.9rem",
                    flexShrink: 0,
                  }}
                >
                  {s.charAt(0)}
                </div>
                <span style={{ fontSize: "clamp(0.85rem, 1.6vw, 1rem)" }}>
                  {s}
                </span>
              </div>
            ))}
          </div>
        </section>
      </div>

      {/* ✅ Responsive Styles */}
      <style>{`
        @media (max-width: 900px) {
          section {
            grid-template-columns: 1fr !important;
            text-align: center;
          }
        }
        @media (max-width: 600px) {
          ul {
            padding-left: 12px !important;
          }
        }
      `}</style>
    </motion.div>
  );
}
